import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Calendar, Clock, ChevronLeft, Mail, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import SectionReveal from "@/components/SectionReveal";
import SEO, { SITE } from "@/components/SEO";
import { getPost, getCategory, getRelatedPosts } from "@/blog/posts";
import { BlogCard } from "@/pages/BlogIndex";

const WHATSAPP = "971589656886";

const BlogPost = () => {
  const { category, slug } = useParams();
  const post = getPost(slug);
  const cat = getCategory(category);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  if (!post || !cat || post.category !== cat.slug) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post.slug, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: [`${SITE.url}${post.cover}`],
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "The China Explorer",
      logo: { "@type": "ImageObject", url: `${SITE.url}/img/og/og-default.jpg` },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE.url}/blog/${post.category}/${post.slug}`,
    },
    keywords: (post.keywords || []).join(", "),
    articleSection: cat.name,
    wordCount: (post.content || "").split(/\s+/).length,
  };

  return (
    <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.category}/${post.slug}`}
        image={post.cover}
        keywords={post.keywords || []}
        type="article"
        jsonLd={jsonLd}
        publishedTime={`${post.date}T00:00:00Z`}
        modifiedTime={`${post.date}T00:00:00Z`}
        author={post.author}
      />
      <Header />

      {/* HERO */}
      <section className="relative h-[60vh] min-h-[440px] w-full overflow-hidden bg-black flex items-end">
        <img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full text-white">
          <Link to={`/blog/${cat.slug}`} data-testid="post-category-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:text-[#D4AF37]">
            <ChevronLeft size={14} /> {cat.name}
          </Link>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">{post.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.2em] text-white/85">
            <span>{post.author}</span>
            <span className="inline-flex items-center gap-2"><Calendar size={12}/> {new Date(post.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
            <span className="inline-flex items-center gap-2"><Clock size={12}/> {post.readMinutes} min read</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="bg-[#F7F5F0] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-lg md:text-xl text-[#4A4A4A] leading-relaxed font-light italic border-l-2 border-[#C62828] pl-5">{post.excerpt}</p>
          <div className="prose-china mt-12" data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* CTA */}
          <SectionReveal className="mt-16 bg-[#0E2A5C] text-white p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">✦ Plan your trip</p>
            <h3 className="font-heading text-2xl md:text-3xl leading-tight">Ready to bring this story to life?</h3>
            <p className="mt-4 text-white/80 leading-relaxed">Tell us your dates, budget and interests. We&apos;ll send a tailored proposal within 24 hours.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#contact" data-testid="post-cta-contact" className="bg-[#C62828] hover:bg-[#B71C1C] text-white px-6 py-3 text-sm uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors">
                <Mail size={14}/> Request Itinerary
              </Link>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer" className="border border-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37] px-6 py-3 text-sm uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors">
                <MessageCircle size={14}/> WhatsApp Us
              </a>
            </div>
          </SectionReveal>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-[#EAE6DF] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Continue reading</p>
            <h3 className="font-heading text-3xl md:text-4xl tracking-tight">More from {cat.name}</h3>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((r, i) => <BlogCard key={r.slug} post={r} delay={i*0.05} />)}
            </div>
            <Link to="/blog" data-testid="back-to-blog-bottom" className="inline-flex items-center gap-2 mt-12 text-sm uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828]">
              <ChevronLeft size={14}/> Back to all articles <ArrowRight size={14}/>
            </Link>
          </div>
        </section>
      )}

      <Footer />
      <AIChat />
    </div>
  );
};

export default BlogPost;
